/**
 * Calculate the relative luminance of a color
 * @param r Red value (0-255)
 * @param g Green value (0-255)
 * @param b Blue value (0-255)
 * @returns Relative luminance value
 */
function getLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map(c => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Parse a color string to RGB values
 * @param color Color string (hex, rgb, rgba, or named color)
 * @returns RGB values or null if parsing fails
 */
function parseColor(color: string): { r: number; g: number; b: number } | null {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return { r, g, b };
    } else if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return { r, g, b };
    }
  }

  // Handle rgb/rgba colors
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1]),
      g: parseInt(rgbMatch[2]),
      b: parseInt(rgbMatch[3])
    };
  }

  return null;
}

/**
 * Determine if white or black text should be used on a given background color
 * @param backgroundColor Background color string
 * @returns 'white' or 'black' for optimal contrast
 */
export function getContrastColor(backgroundColor: string): string {
  const rgb = parseColor(backgroundColor);
  
  if (!rgb) {
    // Default to black if we can't parse the color
    return 'black';
  }

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
  
  // WCAG threshold: luminance > 0.5 suggests a light background
  return luminance > 0.5 ? 'black' : 'white';
}
